import LinkIcon from '@atlaskit/icon/core/link';
import SmartLinkInlineIcon from '@atlaskit/icon/core/smart-link-inline';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Select from '@databases/atlaskit/Select';
import { getCompactSelectStyles } from '@databases/atlaskit/compact-select-styles';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages';

const inputWrapperStyles = xcss({
  maxWidth: '100%',
  width: '266px'
});

const iconContainerStyles = xcss({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  justifyContent: 'center',
  width: '24px'
});

const SmartLinkDisplay = {
  INLINE: 'inline',
  URL: 'url'
} as const;

type SmartLinkDisplay = (typeof SmartLinkDisplay)[keyof typeof SmartLinkDisplay];

type SmartLinkDisplayOption = {
  label: string;
  value: SmartLinkDisplay;
};

type HyperlinkFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.HYPERLINK>;
};

const smartLinkSelectStyles = getCompactSelectStyles<SmartLinkDisplayOption, false>();

const HyperlinkFieldSettings: FunctionComponent<HyperlinkFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const displayAsLabel = formatMessage(messages.smartLinkDisplayAsLabel);
  const options = useMemo<SmartLinkDisplayOption[]>(
    () => [
      {
        label: formatMessage(messages.smartLinkDisplayUrl),
        value: SmartLinkDisplay.URL
      },
      {
        label: formatMessage(messages.smartLinkDisplayInline),
        value: SmartLinkDisplay.INLINE
      }
    ],
    [formatMessage]
  );
  const fieldSelectedValue =
    field.settings.resolve === false ? SmartLinkDisplay.URL : SmartLinkDisplay.INLINE;
  const [selectedValue, setSelectedValue] =
    useState<SmartLinkDisplay>(fieldSelectedValue);
  useEffect(() => {
    setSelectedValue(fieldSelectedValue);
  }, [fieldSelectedValue]);
  const selectedOption = options.find((option) => option.value === selectedValue);

  const handleDisplayChange = useCallback(
    (display: SmartLinkDisplay) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_RESOLVE,
          fieldId: field.id,
          resolve: display === SmartLinkDisplay.INLINE
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleSelectChange = useCallback(
    (option: SmartLinkDisplayOption | null) => {
      const nextValue = option?.value ?? selectedValue;
      setSelectedValue(nextValue);
      handleDisplayChange(nextValue);
    },
    [handleDisplayChange, selectedValue]
  );

  const formatOptionLabel = useCallback((option: SmartLinkDisplayOption) => {
    const icon =
      option.value === SmartLinkDisplay.URL ? (
        <LinkIcon label="" spacing="spacious" testId="smart-link-url-icon" />
      ) : (
        <SmartLinkInlineIcon
          label=""
          spacing="spacious"
          testId="smart-link-inline-icon"
        />
      );

    return (
      <Inline alignBlock="center" space="space.100">
        <Box as="span" xcss={iconContainerStyles}>
          {icon}
        </Box>
        {option.label}
      </Inline>
    );
  }, []);

  return (
    <Stack space="space.050">
      <label htmlFor="smart-link-display-as">
        <Text size="small" weight="bold" color="color.text.subtle">
          {displayAsLabel}
        </Text>
      </label>
      <Box xcss={inputWrapperStyles}>
        <Select<SmartLinkDisplayOption>
          classNamePrefix="smart-link-display-as"
          formatOptionLabel={formatOptionLabel}
          inputId="smart-link-display-as"
          isClearable={false}
          isSearchable={false}
          onChange={handleSelectChange}
          options={options}
          spacing="compact"
          styles={smartLinkSelectStyles}
          value={selectedOption}
        />
      </Box>
    </Stack>
  );
};

export default HyperlinkFieldSettings;
